/*
  Buttons for the cart note (snippets/rw-cart-nudge.liquid).

  The note's forms post to /cart/update and work without JavaScript. Here the
  same update is sent in place: the bundle is added and the sets it replaces
  are removed in one request, then the theme's cart event redraws the drawer or
  the cart page and updates the cart count. If the request fails, the form
  posts normally and the cart page shows the result.

  "Keep ..." hides that note for the rest of the browsing session (session
  storage, with an in-memory fallback), through a style rule in <head>, so it
  stays hidden when the theme redraws the cart.

  Clarity events: rw_nudge_view_<state>, rw_nudge_<state> (swap or remove done),
  rw_nudge_keep_<state>.
*/
import { CartUpdateEvent } from '@theme/events';

const KEPT_KEY = 'rw-nudge-kept';
const keptInMemory = new Set();
const viewed = new Set();

const clarityEvent = (name) => {
  try {
    if (typeof window.clarity === 'function') window.clarity('event', name);
  } catch (error) {
    /* analytics must never break the cart */
  }
};

const readKept = () => {
  const kept = new Set(keptInMemory);
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(KEPT_KEY) || '[]');
    if (Array.isArray(stored)) stored.forEach((key) => kept.add(String(key)));
  } catch (error) {
    /* private browsing or blocked storage: memory only */
  }
  return kept;
};

const applyKept = () => {
  let style = document.getElementById('rw-nudge-kept');
  if (!style) {
    style = document.createElement('style');
    style.id = 'rw-nudge-kept';
    document.head.append(style);
  }
  style.textContent = [...readKept()]
    .map((key) => `rw-cart-nudge[data-key="${CSS.escape(key)}"]{display:none!important}`)
    .join('');
};

class RwCartNudge extends HTMLElement {
  connectedCallback() {
    const { key, state } = this.dataset;
    if (!key || viewed.has(key) || readKept().has(key)) return;
    viewed.add(key);
    clarityEvent(`rw_nudge_view_${state}`);
  }
}

if (!customElements.get('rw-cart-nudge')) {
  customElements.define('rw-cart-nudge', RwCartNudge);
}

applyKept();

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-rw-nudge-keep]') : null;
  const note = button?.closest('rw-cart-nudge');
  if (!note || !note.dataset.key) return;

  const key = note.dataset.key;
  keptInMemory.add(key);
  try {
    const kept = [...readKept()];
    window.sessionStorage.setItem(KEPT_KEY, JSON.stringify(kept));
  } catch (error) {
    /* memory only */
  }
  applyKept();
  clarityEvent(`rw_nudge_keep_${note.dataset.state}`);
});

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-rw-nudge-form')) return;
  event.preventDefault();

  const note = form.closest('rw-cart-nudge');
  const buttons = (note || form).querySelectorAll('button');
  buttons.forEach((button) => {
    button.disabled = true;
  });

  const updates = {};
  for (const [name, value] of new FormData(form)) {
    const match = /^updates\[(.+)\]$/.exec(name);
    if (match) updates[match[1]] = Number(value);
  }

  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);

  try {
    const response = await fetch(`${form.getAttribute('action')}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');

    clarityEvent(`rw_nudge_${note?.dataset.state || 'update'}`);
    (note || document).dispatchEvent(
      new CartUpdateEvent(cart, 'rw-cart-nudge', {
        itemCount: cart.item_count,
        source: 'rw-cart-nudge',
        sections: cart.sections,
      })
    );
  } catch (error) {
    form.submit();
  }
});
